/**
 * ToolCard Component
 * 
 * PREFETCH: O prefetch on hover é automático baseado no tool.id.
 * Para novas ferramentas, adicionar um case correspondente em:
 * → src/hooks/useComponentPrefetch.ts → prefetchPageComponent()
 */

import React, { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Tool, getToolStatus } from '@/data/tools';
import { useRole } from '@/contexts/RoleContext';
import {
  Timer,
  Bot,
  ShoppingBag,
  Lock,
  Clock,
  ArrowRight,
  Handshake,
  Trophy,
  Star,
  Hammer,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { OptimizedImage } from '@/components/OptimizedImage';
import { prefetchPageComponent } from '@/hooks/useComponentPrefetch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Timer,
  Bot,
  ShoppingBag,
  Handshake,
  Trophy,
  Hammer,
  Download,
};

export interface EffectiveToolStatus {
  status: 'active' | 'coming_soon' | 'locked' | 'disabled';
  message?: string;
}

interface ToolCardProps {
  tool: Tool;
  index: number;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  showFavoriteButton?: boolean;
  hideStarButton?: boolean;
  skipAnimation?: boolean;
  backgroundImageUrl?: string | null;
  effectiveStatus?: EffectiveToolStatus;
  /** Optional contextual status line (e.g., "3 notas ativas") */
  statusLine?: string;
}

function ToolCardComponent({ 
  tool, 
  index, 
  isFavorite = false, 
  onToggleFavorite,
  showFavoriteButton = false,
  hideStarButton = false,
  skipAnimation = false,
  backgroundImageUrl,
  effectiveStatus,
  statusLine
}: ToolCardProps) {
  const { effectiveRole } = useRole();
  
  // Prioritize effectiveStatus from database over static status
  const status = effectiveStatus?.status ?? getToolStatus(tool, effectiveRole);
  const disabledMessage = effectiveStatus?.message;
  
  const IconComponent = iconMap[tool.icon] || Timer;

  const isDisabled = status === 'coming_soon' || status === 'locked' || status === 'disabled';

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleFavorite?.();
  };

  // Prefetch component on hover for instant navigation
  const handleMouseEnter = useCallback(() => {
    if (!isDisabled) {
      prefetchPageComponent(tool.id);
    }
  }, [tool.id, isDisabled]);

  const CardContent = () => (
    <div
      className={cn(
        "group relative h-full w-full overflow-hidden",
        isDisabled ? "tool-card-disabled cursor-not-allowed" : "tool-card cursor-pointer"
      )}
    >
      {/* Background Image with dark gradient overlay for legibility */}
      {backgroundImageUrl && (
        <>
          <OptimizedImage
            src={backgroundImageUrl}
            alt=""
            className="absolute inset-0 opacity-[0.05] pointer-events-none"
            decorative
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--background))] via-[hsl(var(--background)/0.8)] to-transparent pointer-events-none" />
        </>
      )}


      {/* Favorite Star Button */}
      {showFavoriteButton && !isDisabled && !hideStarButton && (
        <button
          onClick={handleFavoriteClick}
          className={cn(
            "absolute top-3 left-3 md:top-4 md:left-4 z-20 p-1 md:p-1.5 rounded-full transition-all duration-200",
            "hover:scale-110 active:scale-95",
            isFavorite 
              ? "text-yellow-500" 
              : "text-muted-foreground/50 hover:text-yellow-500/70"
          )}
          aria-label={isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
        >
          <Star 
            className={cn(
              "w-3.5 h-3.5 md:w-4 md:h-4 transition-all",
              isFavorite && "fill-yellow-500"
            )} 
          />
        </button>
      )}

      {/* Glow Effect on Hover */}
      {!isDisabled && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-primary/20 rounded-full blur-[80px]" />
        </div>
      )}

      {/* Status Badge */}
      {status === 'coming_soon' && (
        <div className="absolute top-3 right-3 md:top-4 md:right-4 badge-warning flex items-center gap-0.5 px-1 md:px-1.5 py-0.5">
          <Clock className="w-2 h-2 md:w-2.5 md:h-2.5" />
          <span className="text-[8px] md:text-[9px]">Breve</span>
        </div>
      )}
      {(status === 'locked' || status === 'disabled') && (
        <div className="absolute top-3 right-3 md:top-4 md:right-4 flex items-center gap-0.5 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full bg-destructive/10 text-destructive text-[8px] md:text-[9px] font-semibold border border-destructive/20">
          <Lock className="w-2 h-2 md:w-2.5 md:h-2.5" />
        </div>
      )}

      {/* Content - Centered */}
      <div className="relative z-10 flex flex-col items-center justify-center gap-2.5 md:gap-4">
        {/* Icon */}
        <div className={cn(
          isDisabled ? "icon-wrapper-muted" : "icon-wrapper",
          "w-9 h-9 md:w-11 md:h-11"
        )}>
          <IconComponent className={cn(
            "w-4 h-4 md:w-5 md:h-5 relative z-10",
            isDisabled ? "text-muted-foreground" : "text-primary"
          )} />
        </div>

        {/* Tool Name */}
        <h3 className={cn(
          "font-display text-sm md:text-base font-bold tracking-tight transition-colors text-center leading-tight",
          isDisabled 
            ? "text-muted-foreground" 
            : "text-foreground group-hover:text-primary"
        )}>
          {tool.name}
        </h3>

        {/* Contextual Status Line (e.g., "3 notas ativas") */}
        {!isDisabled && statusLine && (
          <p className="text-[10px] text-muted-foreground text-center leading-tight -mt-1">
            {statusLine}
          </p>
        )}

        {/* Access Denied Message */}
        {(status === 'locked' || status === 'disabled') && (
          <p className="text-[10px] text-muted-foreground/70 text-center leading-tight px-2 -mt-2">
            {disabledMessage || "Você não tem acesso a esta ferramenta"}
          </p>
        )}

        {/* CTA */}
        <div className={cn(
          "inline-flex items-center gap-1.5 font-semibold text-xs transition-all duration-300",
          isDisabled 
            ? "text-muted-foreground/50" 
            : "text-primary group-hover:gap-2"
        )}>
          {isDisabled ? (
            <span>Disponível em breve para alunos</span>
          ) : (
            <>
              <span>Abrir ferramenta</span>
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </>
          )}
        </div>
      </div>

      {/* Bottom Gradient Line */}
      {!isDisabled && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      )}
    </div>
  );

  // Tooltip message for disabled cards
  const getTooltipMessage = () => {
    if (status === 'coming_soon') return 'Esta ferramenta estará disponível em breve';
    if (status === 'locked') return disabledMessage || 'Acesso restrito ao seu nível atual';
    if (status === 'disabled') return disabledMessage || 'Ferramenta temporariamente indisponível';
    return null;
  };

  if (isDisabled) {
    const tooltipMessage = getTooltipMessage();
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="h-full">
              <CardContent />
            </div>
          </TooltipTrigger>
          {tooltipMessage && (
            <TooltipContent 
              side="top" 
              className="max-w-[200px] text-center text-xs"
            >
              {tooltipMessage}
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Link 
      to={tool.route} 
      className="block h-full"
      onMouseEnter={handleMouseEnter}
    >
      <CardContent />
    </Link>
  );
}

// Memoize the component to prevent unnecessary re-renders
const ToolCard = memo(ToolCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.tool.id === nextProps.tool.id &&
    prevProps.index === nextProps.index &&
    prevProps.isFavorite === nextProps.isFavorite &&
    prevProps.showFavoriteButton === nextProps.showFavoriteButton &&
    prevProps.hideStarButton === nextProps.hideStarButton &&
    prevProps.skipAnimation === nextProps.skipAnimation &&
    prevProps.backgroundImageUrl === nextProps.backgroundImageUrl &&
    prevProps.effectiveStatus?.status === nextProps.effectiveStatus?.status
  );
});

export default ToolCard;
